<template>
  <g class="ChartLayer" :transform="`translate(${left}, ${top})`">
    <!-- This component is just for extending, the template is replaced -->
  </g>
</template>

<script>
import { orderBy } from 'lodash';

// const isNumeric = n => !Number.isNaN(parseFloat(n)) && Number.isFinite(n);

export default {
  name: 'DatalasLayer',
  inheritAttrs: false,
  props: {
    id: {
      type: [Number, String],
    },
    chartWidth: {
      type: Number,
      default: 960,
    },
    chartHeight: {
      type: Number,
      default: 600,
    },
    isMobileWidth: Boolean,
    marginTop: {
      type: Number,
      default: 0,
    },
    marginRight: {
      type: Number,
      default: 0,
    },
    marginBottom: {
      type: Number,
      default: 0,
    },
    marginLeft: {
      type: Number,
      default: 0,
    },
    paddingX: {
      type: Number,
      default: 0,
    },
    paddingY: {
      type: Number,
      default: 0,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    transforms: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    axes: {
      type: Array,
      default: () => [],
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    labelFormat: {
      type: String,
      default: '',
    },
    labelPrefix: {
      type: String,
      default: '',
    },
    labelSuffix: {
      type: String,
      default: '',
    },
    noLabelCollisions: {
      type: Boolean,
      default: false,
    },
    labelInside: {
      type: Boolean,
      default: false,
    },
    labelFilters: {
      type: Array,
    },
    labelAnchor: {
      type: String,
    },
    labelBaseline: {
      type: String,
    },
    labelOffsetX: {
      type: Number,
      default: 0,
    },
    labelOffsetY: {
      type: Number,
      default: 0,
    },
    labelFontSize: {
      type: Number,
      default: 14,
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    gridKey: {
      type: String,
      default: 'grid',
    },
    gridMounted: Boolean,
    preprocess: {
      type: [Array, Function],
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
    sorts: {
      type: [Array, Function],
      default: () => [],
    },
    postprocess: {
      type: [Array, Function],
      default: () => [],
    },
  },

  data: () => ({
    // Note: layers can and should override these if they use abnormal keys
    // for x and y (eg. length and bands on the bar chart)
    xDimension: 'x',
    yDimension: 'y',
  }),

  computed: {
    preprocessors() {
      return typeof this.preprocess === 'function'
        ? [this.preprocess]
        : this.preprocess;
    },
    postprocessors() {
      return typeof this.postprocess === 'function'
        ? [this.postprocess]
        : this.postprocess;
    },
    data() {
      return this.preprocessors
        .concat([this.filterData, this.sortData], this.postprocessors)
        .reduce((data, p) => p(data.slice()), this.chartData);
    },
    alignValue() {
      return this.align || (this.context && this.context.align) || 'bottom';
    },
    top() {
      return this.marginTop || this.marginTop === 0
        ? this.marginTop
        : this.context.marginTop || 0;
    },
    right() {
      return this.marginRight || this.marginRight === 0
        ? this.marginRight
        : this.context.marginRight || 0;
    },
    bottom() {
      return this.marginBottom || this.marginBottom === 0
        ? this.marginBottom
        : this.context.marginBottom || 0;
    },
    left() {
      return this.marginLeft || this.marginLeft === 0
        ? this.marginLeft
        : this.context.marginLeft || 0;
    },
    outerWidth() {
      return this.chartWidth - this.left - this.right;
    },
    outerHeight() {
      return this.chartHeight - this.top - this.bottom;
    },
    width() {
      return this.outerWidth - this.paddingX * 2;
    },
    height() {
      return this.outerHeight - this.paddingY * 2;
    },
    isHorizontal() {
      return ['left', 'right'].includes(this.alignValue);
    },
    labelAnchorVal() {
      return (
        this.labelAnchor ||
        (this.labelInside && 'middle') ||
        {
          top: 'middle',
          right: 'end',
          bottom: 'middle',
          left: 'start',
        }[this.alignValue]
      );
    },
    labelBaselineVal() {
      return (
        this.labelBaseline ||
        (this.labelInside && 'middle') ||
        {
          top: 'hanging',
          right: 'middle',
          bottom: undefined,
          left: 'middle',
        }[this.alignValue]
      );
    },
    axesFlipMap() {
      // This is used to help automatically fix axes alignments that are
      // illogical, eg. align: left on a horizontal x-axis.
      return {
        [this.isHorizontal ? this.yDimension : this.xDimension]: {
          left: 'bottom',
          right: 'top',
        },
        [this.isHorizontal ? this.xDimension : this.yDimension]: {
          top: 'right',
          bottom: 'left',
        },
      };
    },
    grid() {
      const { outerWidth, outerHeight } = this;
      return {
        top: this.top,
        left: this.left,
        axes: this.axes
          .map((axis) => {
            const props = { ...axis };
            const { orient, scale } = axis;

            const { dimension } =
              this.dimensions.find((d) => d.transform === scale) || {};

            if (!dimension) return null;

            const o = this.axesFlipMap[dimension][orient] || orient;

            props.orient = o;
            props.paddingX = this.paddingX;
            props.paddingY = this.paddingY;

            return {
              props,
              xPos: { top: 0, right: outerWidth, left: -1, bottom: -1 }[o],
              yPos: { top: -1, right: -1, left: 0, bottom: outerHeight }[o],
            };
          })
          .filter((a) => a),
      };
    },
  },

  methods: {
    filterData(data) {
      return data.filter((datum) =>
        this.filters.every((filter) => {
          const field = datum[filter.field];

          if (filter.isNot !== undefined) {
            return field !== filter.isNot;
          }

          if (filter.is !== undefined) {
            return field === filter.is || field === parseInt(filter.is, 10);
          }

          // This currently isn't used, but works
          if (filter.exclude && filter.exclude.length > 0) {
            return !filter.exclude.find((x) => x === field);
          }

          if (filter.includeOnly && filter.includeOnly.length > 0) {
            return filter.includeOnly.find((x) => x === field);
          }

          if (
            filter.min ||
            filter.min === 0 ||
            filter.max ||
            filter.max === 0
          ) {
            const min = filter.min === undefined ? 0 : filter.min;
            const max = filter.max === undefined ? 99999999 : filter.max;
            return field >= min && field <= max;
          }

          return true;
        }),
      );
    },
    sortData(data) {
      if (typeof this.sorts === 'function') {
        return data.slice().sort(this.sorts);
      }

      const fields = this.sorts.map((s) => s.field);
      const orders = this.sorts.map((s) => s.order || 'asc');

      return orderBy(data, fields, orders);
    },
    getField(field, id) {
      const fieldObj = this.fields[field];
      return fieldObj ? fieldObj.filter((f) => f.id === id)[0] || {} : {};
    },
    getFieldValue(field, id, key) {
      return this.getField(field, id)[key];
    },
    getDimensionTransform(dimension) {
      const { transform } = this.dimensions.find(
        (d) => d.dimension === dimension,
      );
      return this.transforms[transform];
    },
  },
};
</script>
